import {
  Scale,
  Target,
  Waypoints,
  Server,
  FileText,
  CalendarClock,
  Trash2,
  ArrowRight,
} from 'lucide-react';
import { Badge, Card, CardBody } from '../../ui';
import {
  formatDate,
  isRevisitDue,
  revisitInDays,
} from '../../features/decisions/decisionModel';
import type { DecisionSummary } from '../../features/decisions/types';

export interface DecisionCardProps {
  decision: DecisionSummary;
  goalTitle?: string;
  workstreamTitle?: string;
  docTitle?: string;
  serviceTitles: Map<string, string>;
  onOpen?: (id: string) => void;
  onDelete?: (decision: DecisionSummary) => void;
}

/** A single Decision Log card — decision, chosen option, why, and links. */
export function DecisionCard({
  decision,
  goalTitle,
  workstreamTitle,
  docTitle,
  serviceTitles,
  onOpen,
  onDelete,
}: DecisionCardProps) {
  const revisitDays = revisitInDays(decision);
  const due = isRevisitDue(decision);

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-start gap-2">
            <Scale className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
            <button
              type="button"
              onClick={() => onOpen?.(decision.id)}
              disabled={!onOpen}
              className="group min-w-0 text-left"
            >
              <span className="block truncate text-sm font-semibold text-zinc-100 group-hover:text-white">
                {decision.decision || 'Untitled decision'}
              </span>
            </button>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {decision.revisitDate && (
              <Badge tone={due ? 'danger' : 'info'}>
                <CalendarClock className="h-3 w-3" />
                {due
                  ? `revisit overdue ${Math.abs(revisitDays ?? 0)}d`
                  : `revisit in ${revisitDays ?? 0}d`}
              </Badge>
            )}
            {onDelete && (
              <button
                type="button"
                onClick={() => onDelete(decision)}
                aria-label="Delete decision"
                className="rounded-md p-1 text-zinc-500 transition-colors hover:bg-surface-3 hover:text-red-300"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        {decision.decisionMade && (
          <p className="text-sm text-zinc-200">
            <span className="text-zinc-500">Chose: </span>
            {decision.decisionMade}
          </p>
        )}
        {decision.why && (
          <p className="line-clamp-2 text-xs text-zinc-400">
            <span className="text-zinc-500">Why: </span>
            {decision.why}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
          {decision.owner && (
            <span>
              Owner: <span className="text-zinc-300">{decision.owner}</span>
            </span>
          )}
          {decision.date && <span>{formatDate(decision.date)}</span>}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {decision.relatedGoalId && (
            <Badge tone="accent"><Target className="h-3 w-3" /> {goalTitle ?? 'Goal'}</Badge>
          )}
          {decision.relatedWorkstreamId && (
            <Badge tone="info"><Waypoints className="h-3 w-3" /> {workstreamTitle ?? 'Workstream'}</Badge>
          )}
          {decision.relatedDocId && (
            <Badge tone="neutral"><FileText className="h-3 w-3" /> {docTitle ?? 'Doc'}</Badge>
          )}
          {decision.affectedServices.map((s) => (
            <Badge key={s} tone="neutral"><Server className="h-3 w-3" /> {serviceTitles.get(s) ?? s}</Badge>
          ))}
        </div>

        {onOpen && (
          <button
            type="button"
            onClick={() => onOpen(decision.id)}
            className="group inline-flex items-center gap-1 text-xs text-zinc-500 transition-colors hover:text-zinc-300"
          >
            Details
            <ArrowRight className="h-3.5 w-3.5 transition-colors group-hover:text-zinc-300" />
          </button>
        )}
      </CardBody>
    </Card>
  );
}

export default DecisionCard;
